/** Helpers de filtros NFS-e / agentes */

export function digitos(v) {
  return String(v || '').replace(/\D/g, '');
}

export function formatCnpjCpfMask(value) {
  const d = digitos(value);
  if (d.length === 11) {
    return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (d.length === 14) {
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return d || '—';
}

/** Primeiro e último dia do mês atual (Date locais). */
export function mesAtualRange() {
  const now = new Date();
  const inicio = new Date(now.getFullYear(), now.getMonth(), 1);
  const fim = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  inicio.setHours(0, 0, 0, 0);
  fim.setHours(23, 59, 59, 999);
  return { dataInicio: inicio, dataFim: fim };
}

export function labelEmpresaFilial(entidade, fallback = 'Sem nome') {
  if (!entidade) return fallback;
  return entidade.fantasia || entidade.razaosocial || fallback;
}

/** Monta options ricos: Empresa matriz (value 0) + filiais. */
export function buildFilialFilterOptions(empresa, filiais = [], { incluirTodas = true } = {}) {
  const opts = [];
  if (incluirTodas) {
    opts.push({
      label: 'Todas',
      value: null,
      razao: 'Todas',
      fantasia: '',
      cnpjcpf: '',
      search: 'todas',
    });
  }
  const empresaLabel = labelEmpresaFilial(empresa, 'Empresa principal');
  opts.push({
    label: `${empresaLabel} (matriz)`,
    value: 0,
    isMatriz: true,
    razao: empresa?.razaosocial || empresaLabel,
    fantasia: empresa?.fantasia || 'Empresa principal',
    cnpjcpf: digitos(empresa?.cnpjcpf || empresa?.cnpj || empresa?.cnpjCpf || ''),
    search: `${empresa?.razaosocial || ''} ${empresa?.fantasia || ''} ${empresa?.cnpjcpf || empresa?.cnpj || ''} matriz`.toLowerCase(),
  });
  (filiais || []).forEach((f) => {
    const razao = f.razaosocial || '';
    const fantasia = f.fantasia || '';
    const nome = fantasia || razao || `Filial ${f.id}`;
    opts.push({
      label: nome,
      value: f.id,
      razao,
      fantasia,
      cnpjcpf: digitos(f.cnpjcpf),
      search: `${razao} ${fantasia} ${f.cnpjcpf || ''}`.toLowerCase(),
    });
  });
  return opts;
}

/** Options de agentes com metadados; filtra por filial se informado.
 *  modo:
 *  - 'filtro' (padrão): matriz (0) = só agentes sem filial; filial = só daquela filial
 *  - 'emissao': matriz (null/undefined) = todos; filial = da filial + compartilhados (sem filial)
 */
export function buildAgenteFilterOptions(
  agentes = [],
  { filialId = null, incluirTodos = true, todosLabel = 'Todos', modo = 'filtro', tipo = null } = {}
) {
  let list = agentes || [];
  if (tipo) {
    list = list.filter((a) => !a.tipo || a.tipo === tipo);
  }

  if (modo === 'emissao') {
    if (filialId != null && filialId !== '') {
      const fid = Number(filialId);
      list = list.filter((a) => {
        if (a.filialId == null || a.filialId === undefined || a.filialId === '') return true;
        return Number(a.filialId) === fid;
      });
    }
    // matriz / sem filtro: todos os agentes (da empresa)
  } else if (filialId === 0) {
    list = list.filter((a) => a.filialId == null || a.filialId === undefined || a.filialId === '');
  } else if (filialId != null && filialId !== '') {
    const fid = Number(filialId);
    list = list.filter((a) => Number(a.filialId) === fid);
  }

  const opts = [];
  if (incluirTodos) {
    opts.push({
      label: todosLabel,
      value: null,
      razao: todosLabel,
      fantasia: '',
      cnpjcpf: '',
      search: todosLabel.toLowerCase(),
    });
  }
  list.forEach((a) => {
    const razao = a.razaosocial || '';
    const fantasia = a.fantasia || '';
    const nome = fantasia || razao || a.cnpjcpf || `Agente ${a.id}`;
    opts.push({
      label: nome,
      value: a.id,
      razao,
      fantasia,
      cnpjcpf: digitos(a.cnpjcpf),
      search: `${razao} ${fantasia} ${a.cnpjcpf || ''} ${a.cidade || ''}`.toLowerCase(),
    });
  });
  return opts;
}

/** Cabeçalhos de autenticação (Bearer + empresa ativa) para downloads via fetch. */
export function authHeaders() {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const empresaId = sessionStorage.getItem('empresaId');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (empresaId) headers['X-Empresa-Id'] = empresaId;
  return headers;
}

/** Baixa um arquivo (blob) de um endpoint de export usando os headers de autenticação atuais. */
export async function downloadFromApi(apiBaseUrl, path, params, fallbackName) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && v !== '') qs.set(k, String(v));
  });
  const url = `${apiBaseUrl}${path}${qs.toString() ? `?${qs.toString()}` : ''}`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  const fileName = m ? decodeURIComponent(m[1].replace(/"/g, '')) : fallbackName;
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
  return fileName;
}

/** Abre um PDF (base64 ou texto %PDF) numa nova aba. */
export function openPdfFromContent(content, fileName) {
  let bytes;
  if (typeof content === 'string' && content.trim().startsWith('%PDF')) {
    bytes = new TextEncoder().encode(content);
  } else {
    const bin = atob(content);
    bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return fileName;
}
